/** An info control whose text is a fact the server just worked out - the classification's own
 * `because` list - rather than a paragraph of the product's (`InfoPopover` takes only keys into
 * `infoText.ts`, which is how a screen cannot invent a paragraph). The text here is data, not copy:
 * it is shown, in the server's words, only behind this control, and it is not part of the screen's
 * body text. Same card, same keyboard behaviour: Escape closes and returns focus, Tab leaves.
 */

import { Info } from "@phosphor-icons/react";
import type { KeyboardEvent } from "react";
import { useEffect, useId, useRef, useState } from "react";

import { PreviewCard } from "../../components/ui";
import { usePopup } from "../../components/ui/usePopup";
import styles from "../../components/ui/InfoPopover.module.css";

export function ReasonPopover({ title, reasons }: { title: string; reasons: readonly string[] }) {
  const { open, setOpen, close, triggerRef, popupRef } = usePopup<HTMLButtonElement>();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (open) bodyRef.current?.focus();
  }, [open]);

  if (reasons.length === 0) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab" && open) {
      close();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={`Why: ${title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          if (open) {
            close(false);
            return;
          }
          setAnchor(triggerRef.current?.getBoundingClientRect() ?? null);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      >
        <Info size={14} weight="bold" aria-hidden="true" />
      </button>
      {open && anchor ? (
        <PreviewCard
          anchor={anchor}
          id={id}
          role="dialog"
          ariaLabel={title}
          testId="reason-popover"
          className={styles.card}
          popupRef={popupRef}
          onKeyDown={onKeyDown}
          align="end"
        >
          <div ref={bodyRef} tabIndex={-1} className={styles.body}>
            <p className={styles.title}>{title}</p>
            {reasons.map((reason) => (
              <p key={reason} className={styles.text}>
                {reason}
              </p>
            ))}
          </div>
        </PreviewCard>
      ) : null}
    </>
  );
}
